import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useDomLoaded } from '../../utils/hooks';

const Flickity = process.env.NODE_ENV === 'test'
  ? require('react-flickity-component')
  : dynamic(
    () => import('react-flickity-component'),
    { ssr: false }
  );

const CHILD_CLASS = 'carousel-cell';

const carouselBaseStyles = css`
  position: relative;

  .${CHILD_CLASS} {
    height: auto;
    position: relative;
    width: 100%;
  }
`;

const carouselArrowStyles = (theme) => css`
  .flickity-prev-next-button {
    background: transparent;
    border-radius: 100%;
    border: 1px solid ${theme.color.brand};
    color: transparent;
    cursor: pointer;
    display: block;
    font-size: 0;
    height: 3rem;
    line-height: 0;
    opacity: .75;
    outline: none;
    padding: 0;
    position: absolute;
    stroke: ${theme.color.brand};
    top: 50%;
    transform: translate(0, -50%);
    transition: opacity ${theme.animation.default} ${theme.animation.easeOutQuad};
    width: 3rem;

    :hover {
      background: transparent;
      color: transparent;
      opacity: 1;
      outline: none;
    }

    svg {
      fill: ${theme.color.brand};
      height: 1rem;
      stroke-width: 2;
      width: 1rem;
    }

    &.previous { left: -3rem; }

    &.next {
      right: -3rem;
      left: auto;
    }

    :disabled {
      cursor: not-allowed;
      opacity: .25;
    }
  }
`;

const carouselDotsStyles = (theme) => css`
  .flickity-page-dots {
    display: flex;
    height: 3rem;
    justify-content: center;
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: center;
    width: 100%;

    .dot {
      align-items: center;
      cursor: pointer;
      display: inline-flex;
      height: 3rem;
      justify-content: center;
      padding: 0;
      position: relative;
      width: 3rem;

      ::before {
        background: ${theme.color.brandLight};
        content: '';
        height: 0.8rem;
        width: 0.8rem;
        border-radius: 99999px;
        transition: background ${theme.animation.default} ${theme.animation.easeOutQuad};
      }

      :hover {
        ::before {
          opacity: 1;
          background: ${theme.color.brand};
        }
      }

      &.is-selected {
        ::before {
          opacity: .75;
          background: ${theme.color.brand};
        }
      }
    }
  }
`;

const carouselCellStyles = ({ type, theme }) => {
  const styles = css`
    ${carouselBaseStyles}
    ${carouselArrowStyles(theme)}
    :not(.no-dots) {
      ${carouselDotsStyles(theme)}
    }
  `;

  if (type === 'product-card') {
    return css`
      ${styles}
      margin: 3rem;
      position: relative;

      .flickity-viewport {
        overflow: hidden;
      }

      .${CHILD_CLASS} {
        @media screen and (min-width: ${theme.breakpoint.small}) {
          width: 50%;
        }

        @media screen and (min-width: ${theme.breakpoint.medium}) {
          width: 20%;
        }
      }
    `;
  }

  if (type === 'three-ways') {
    return css`
      ${styles}

      .flickity-viewport {
        overflow: hidden;
      }

      .${CHILD_CLASS} {
        @media screen and (min-width: ${theme.breakpoint.medium}) {
          width: 33.33333%;
        }
      }
    `;
  }

  if (type === 'taxon-products') {
    return css`
      ${styles}

      .flickity-viewport {
        overflow: hidden;
      }

      .${CHILD_CLASS} {
        @media screen and (min-width: ${theme.breakpoint.small}) {
          width: 50%;
        }

        @media screen and (min-width: ${theme.breakpoint.medium}) {
          width: 25%;
        }

        @media screen and (min-width: ${theme.breakpoint.large}) {
          width: 20%;
        }
      }
    `;
  }

  if (type === 'hero') {
    return css`
      ${styles}

      .${CHILD_CLASS} {
        margin-right: 1rem;
      }
    `;
  }

  if (type === 'quickshop') {
    return css`
      ${styles}
      overflow: hidden;
      margin: 0 3rem;
    `;
  }

  if (type === 'pdp') {
    return css`
      ${styles}
      margin: 0 3rem;

      .flickity-viewport {
        overflow: hidden;
      }

      .${CHILD_CLASS} {
        padding: 2rem;
      }
    `;
  }

  return styles;
};

const StyledFlickity = styled(Flickity)`
  ${carouselCellStyles}
`;

const FlickityPlaceHolder = styled.div`
  ${carouselCellStyles}
  display: flex;
  overflow: hidden;

  > * {
    flex: 0 0 auto;
  }
`;

const Carousel = (props) => {
  const isDomReady = useDomLoaded();

  const defaultOptions = {
    arrowShape: 'M 46.19 3.452 L 53.333 10.119 L 18.571 45.357 L 100 45.357 L 100 54.881 L 18.571 54.881 L 53.571 90.119 L 47.381 96.548 L 0 50.357 L 46.19 3.452 Z'
  };

  const options = {
    ...defaultOptions,
    ...props.options
  };

  const children = React.Children.map(props.children, (child) => {
    const childProps = {
      ...child.props,
      className: `${CHILD_CLASS} ${child.className ?? ''}`
    };

    return React.cloneElement(child, childProps);
  });

  return (
    <>
      {
        isDomReady
          ? (
            <StyledFlickity
              className={props.className}
              disableImagesLoaded={props.disableImagesLoaded}
              elementType={props.elementType}
              flickityRef={props.flickityRef}
              options={options}
              reloadOnUpdate={props.reloadOnUpdate}
              static={props.static}
              type={props.type}
            >
              {children}
            </StyledFlickity>
          )
          : (
            <FlickityPlaceHolder
              type={props.type}
            >
              {children}
            </FlickityPlaceHolder>
          )
      }
    </>
  );
};

Carousel.defaultProps = {
  children: null,
  className: '',
  disableImagesLoaded: false,
  elementType: 'div',
  flickityRef: () => {},
  options: {},
  reloadOnUpdate: false,
  static: false,
  type: undefined
};

Carousel.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  disableImagesLoaded: PropTypes.bool,
  elementType: PropTypes.string,
  flickityRef: PropTypes.func,
  options: PropTypes.object,
  reloadOnUpdate: PropTypes.bool,
  static: PropTypes.bool,
  type: PropTypes.oneOf([
    undefined,
    'product-card',
    'pdp',
    'three-ways',
    'taxon-products',
    'quickshop',
    'hero'
  ])
};

Carousel.whyDidYouRender = true;

export default Carousel;
